import { ListTileContent } from "@/components/list-tile-content";
import { PageActionButton } from "@/components/page-action-button";
import { useEffect, useId, useState } from "react";
import { FolderInput, ListPlus, MoreHorizontal, Plus, Trash2, Ungroup, X } from "lucide-react";
import { api } from "@/api";
import { SphereBusinessControls } from "@/components/business-marketplace-page";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { educationGroupReturnFocus, EducationItemGroupingMenu } from "@/components/education-item-groups";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle,
} from "@/components/form-surface";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { canAutofocusForm, useIsMobile } from "@/hooks/use-mobile";
import { useSphereSharing } from "@/lib/sphere-sharing";
import {
  educationListItemCount,
  UNLISTED_EDUCATION_LIST_ID,
} from "@/lib/education-lists";

export function EducationItemListMenu({
  currentListId,
  disabled = false,
  grouping,
  itemLabel,
  itemTitle,
  lists,
  moving = false,
  onCreateList,
  onDelete,
  onRemoveFromGroup,
  onMove,
  removeBusy = false,
}) {
  const { readOnly } = useSphereSharing();
  if (readOnly) return null;
  const selectedListId = currentListId || UNLISTED_EDUCATION_LIST_ID;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={(
          <Button
            className="pointer-events-auto relative z-20 size-(--layout-control-size) rounded-full"
            variant="ghost"
            size="icon-lg"
            type="button"
            disabled={disabled}
          />
        )}
        aria-label={grouping || onRemoveFromGroup || onDelete ? `Действия: ${itemTitle}` : `Переместить ${itemLabel} «${itemTitle}» в другой список`}
      >
        {moving ? <Spinner className="size-5" aria-hidden="true" /> : <MoreHorizontal className="size-5" aria-hidden="true" />}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="rollapp-body w-(--layout-menu-wide-width) max-w-(--available-width) rounded-3xl p-3"
        align="end"
        sideOffset={8}
      >
        {grouping && <>
          <EducationItemGroupingMenu {...grouping} busy={disabled || moving || grouping.busy} />
          <DropdownMenuSeparator className="my-2" />
        </>}
        <DropdownMenuGroup>
          <DropdownMenuLabel className="px-2 py-2 text-base">Списки</DropdownMenuLabel>
          {onCreateList && (
            <DropdownMenuItem
              className="min-h-14 gap-3 rounded-2xl px-2 py-2 text-base"
              disabled={moving}
              onClick={onCreateList}
            >
              <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-muted text-muted-foreground" aria-hidden="true">
                <ListPlus className="size-6" />
              </span>
              Новый список
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>
        {onCreateList && <DropdownMenuSeparator className="my-2" />}
        <DropdownMenuRadioGroup
          className="max-h-[22.75rem] overflow-y-auto overscroll-contain"
          value={selectedListId}
          onValueChange={(listId) => listId !== selectedListId && onMove(listId)}
        >
          <DropdownMenuRadioItem
            className="min-h-14 gap-3 rounded-2xl px-2 py-2 pr-10 text-base"
            value={UNLISTED_EDUCATION_LIST_ID}
            disabled={moving}
          >
            <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-muted text-muted-foreground" aria-hidden="true">
              <FolderInput className="size-6" />
            </span>
            <span className="min-w-0 flex-1 truncate">Не отсортированные</span>
          </DropdownMenuRadioItem>
          {lists.map((list) => (
            <DropdownMenuRadioItem
              className="min-h-14 gap-3 rounded-2xl px-2 py-2 pr-10 text-base"
              value={list.id}
              key={list.id}
              disabled={moving}
            >
              <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-muted text-muted-foreground" aria-hidden="true">
                <ListPlus className="size-6" />
              </span>
              <span className="min-w-0 flex-1 truncate">{list.title}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        {onDelete && (
          <>
            <DropdownMenuSeparator className="my-2" />
            <DropdownMenuItem
              variant="destructive"
              className="app-destructive-menu-item min-h-14 gap-3 rounded-2xl px-3 text-base"
              disabled={moving}
              aria-haspopup="dialog"
              onClick={onDelete}
            >
              <Trash2 aria-hidden="true" />
              Удалить
            </DropdownMenuItem>
          </>
        )}
        {onRemoveFromGroup && (
          <>
            <DropdownMenuSeparator className="my-2" />
            <DropdownMenuItem
              variant="destructive"
              className="app-destructive-menu-item min-h-14 gap-3 rounded-2xl px-3 text-base"
              disabled={moving || removeBusy}
              onClick={onRemoveFromGroup}
            >
              {removeBusy ? <Spinner aria-hidden="true" /> : <Ungroup aria-hidden="true" />}
              Убрать из группы
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function EducationSectionHeader({ title, titleId, selectedList, onAdd, onEditList, sphereId = "education" }) {
  const { readOnly } = useSphereSharing();
  if (readOnly) return <h2 className="sr-only" id={titleId}>{title}</h2>;
  return (
    <header className="page-toolbar w-full justify-center">
      <h2 className="sr-only" id={titleId}>{title}</h2>
      <div className="page-actions horizontal-action-scroller" role="group" aria-label={`Действия раздела «${title}»`}>
        {selectedList && (
          <PageActionButton
            className="shrink-0"
            variant="outline"
            type="button"
            onClick={() => onEditList(selectedList)}
          >
            Настройки списка
          </PageActionButton>
        )}
        <PageActionButton
          className="shrink-0 min-w-[180px] max-[560px]:min-w-0"
          type="button"
          onClick={onAdd}
        >
          Добавить
        </PageActionButton>
        <SphereBusinessControls sphereId={sphereId} />
      </div>
    </header>
  );
}

export function EducationListNavigation({
  items,
  lists,
  selectedListId,
  unlistedTitle = "Не отсортированные",
  ariaLabel,
  onCreateList,
  onSelectList,
}) {
  const { readOnly } = useSphereSharing();
  const unlistedCount = educationListItemCount(items, UNLISTED_EDUCATION_LIST_ID);
  const showUnlisted = unlistedCount > 0 || lists.length === 0;
  return (
    <div className="min-w-0" data-group-navigation>
      <div className="list-tabs horizontal-tile-scroller mb-0">
        <div className="list-tabs__track">
          <ToggleGroup
            className="contents"
            value={[selectedListId]}
            onValueChange={(values) => values[0] && onSelectList(values[0])}
            aria-label={ariaLabel}
          >
            {showUnlisted && (
              <ToggleGroupItem
                data-list-tile=""
                value={UNLISTED_EDUCATION_LIST_ID}
                aria-label={`${unlistedTitle}, ${unlistedCount}`}
              >
                <ListTileContent title={unlistedTitle} count={unlistedCount} />
              </ToggleGroupItem>
            )}
            {lists.map((list) => {
              const count = educationListItemCount(items, list.id);
              return (
                <ToggleGroupItem
                  data-list-tile=""
                  value={list.id}
                  key={list.id}
                  aria-label={`${list.title}, ${count}`}
                >
                  <ListTileContent title={list.title} count={count} />
                </ToggleGroupItem>
              );
            })}
          </ToggleGroup>
          {!readOnly && <Button
            variant="ghost"
            size="icon"
            className="list-tabs__add"
            aria-label="Новый список"
            title="Новый список"
            type="button"
            onClick={onCreateList}
          >
            <Plus size={16} />
          </Button>}
        </div>
      </div>
    </div>
  );
}

export function EducationListDrawer({
  list,
  open,
  section,
  itemPlural,
  onDeleted,
  onOpenChange,
  onSaved,
}) {
  const isMobile = useIsMobile();
  const titleId = useId();
  const descriptionId = useId();
  const editing = Boolean(list?.id);
  const [form, setForm] = useState({ title: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm({ title: list?.title || "", description: list?.description || "" });
    setSaving(false);
    setDeleting(false);
    setDeleteOpen(false);
    setError("");
  }, [list, open]);

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const result = editing
        ? await api.patch(`/education/lists/${encodeURIComponent(list.id)}`, form)
        : await api.post("/education/lists", { ...form, section });
      onSaved(result.list);
      onOpenChange(false);
    } catch (requestError) {
      setError(requestError.message);
      setSaving(false);
    }
  }

  async function remove() {
    if (!editing || deleting) return;
    setDeleting(true);
    setError("");
    try {
      const result = await api.delete(`/education/lists/${encodeURIComponent(list.id)}`);
      setDeleteOpen(false);
      onDeleted(result);
      onOpenChange(false);
    } catch (requestError) {
      setDeleteOpen(false);
      setError(requestError.message);
      setDeleting(false);
    }
  }

  return (
    <>
      <Drawer
        open={open}
        showSwipeHandle
        swipeDirection={isMobile ? "down" : "right"}
        onOpenChange={(nextOpen) => !saving && !deleting && onOpenChange(nextOpen)}
      >
        <DrawerContent
          className="rollapp-body app-drawer--form"
          finalFocus={educationGroupReturnFocus}
        >
          <DrawerClose
            render={<Button className="absolute top-2 right-2 z-10 size-12" variant="ghost" size="icon" type="button" disabled={saving || deleting} />}
            aria-label="Закрыть настройки списка"
          >
            <X aria-hidden="true" />
          </DrawerClose>
          <form className="flex min-h-0 min-w-0 flex-1 flex-col" onSubmit={submit}>
            <DrawerHeader className="pr-16 text-left!">
              <DrawerTitle>{editing ? "Изменить список" : "Создать список"}</DrawerTitle>
              <DrawerDescription>
                {editing ? "Измените название и описание списка." : `Соберите связанные ${itemPlural} в отдельный список.`}
              </DrawerDescription>
            </DrawerHeader>
            <div className="app-drawer-body flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
              {error && (
                <Alert variant="destructive">
                  <AlertTitle>Не удалось сохранить список</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <FieldGroup className="gap-4">
                <Field>
                  <FieldLabel htmlFor={titleId}>Название</FieldLabel>
                  <Input
                    className="min-h-12 text-base"
                    id={titleId}
                    autoFocus={canAutofocusForm()}
                    required
                    maxLength={80}
                    placeholder="Например, На этот год"
                    value={form.title}
                    onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor={descriptionId}>Описание</FieldLabel>
                  <Textarea
                    className="min-h-24 text-base"
                    id={descriptionId}
                    rows={4}
                    maxLength={300}
                    placeholder="Коротко опишите назначение списка"
                    value={form.description}
                    onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                  />
                </Field>
              </FieldGroup>
              {editing && (
                <div className="mt-auto flex items-center justify-between gap-4 border-t pt-4">
                  <div className="min-w-0">
                    <strong className="block text-sm font-medium">Удалить список</strong>
                    <span className="text-sm text-muted-foreground">Все записи останутся в разделе без списка.</span>
                  </div>
                  <Button type="button" variant="destructive" disabled={deleting} onClick={() => setDeleteOpen(true)}>
                    <Trash2 data-icon="inline-start" aria-hidden="true" />
                    Удалить
                  </Button>
                </div>
              )}
            </div>
            <DrawerFooter>
              <Button className="min-h-12 px-4 text-base" type="submit" disabled={saving || deleting}>
                {saving && <Spinner aria-hidden="true" />}
                {saving ? "Сохраняем" : editing ? "Сохранить изменения" : "Создать список"}
              </Button>
              <DrawerClose render={<Button className="min-h-12 px-4 text-base" variant="outline" type="button" disabled={saving || deleting} />}>
                Отмена
              </DrawerClose>
            </DrawerFooter>
          </form>
        </DrawerContent>
      </Drawer>
      {deleteOpen && (
        <AlertDialog open={deleteOpen} onOpenChange={(nextOpen) => !deleting && setDeleteOpen(nextOpen)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Удалить «{list?.title}»?</AlertDialogTitle>
              <AlertDialogDescription>
                Все записи останутся в разделе и перейдут в «Не отсортированные». Отменить удаление списка не получится.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Отмена</AlertDialogCancel>
              <AlertDialogAction variant="destructive" disabled={deleting} onClick={remove}>
                {deleting ? <Spinner data-icon="inline-start" /> : <Trash2 data-icon="inline-start" aria-hidden="true" />}
                Удалить
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}
